import { useEffect, useState } from "react";
import { Save, Mail, CreditCard, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

export default function SuperSettings() {
  const [smtp, setSmtp] = useState({ host: "", port: 465, user: "", password: "", from_email: "" });
  const [smtpPwSaved, setSmtpPwSaved] = useState(false);
  const [stripeCfg, setStripeCfg] = useState({ mode: "sandbox", secret_key_last4: "", publishable_key: "", webhook_secret_set: false });
  const [stripeForm, setStripeForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [verifying, setVerifying] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => {
      setSmtp({ host: r.data.smtp.host, port: r.data.smtp.port, user: r.data.smtp.user, password: "", from_email: r.data.smtp.from_email });
      setSmtpPwSaved(r.data.smtp.password_set);
      setStripeCfg(r.data.stripe);
      setStripeForm({ secret_key: "", publishable_key: r.data.stripe.publishable_key || "", webhook_secret: "" });
    }).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, []);

  const saveSmtp = async () => {
    try {
      await api.put("/admin/settings", { smtp });
      toast.success("Platform SMTP saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verifyStripe = async () => {
    if (!stripeForm.secret_key.trim()) return toast.error("Paste the secret key first");
    setVerifying(true);
    try {
      const { data } = await api.post("/admin/settings/stripe/verify", { secret_key: stripeForm.secret_key });
      toast.success(`Key works — account ${data.account_id} (${data.country}), ${data.livemode ? "LIVE mode" : "test mode"}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const saveStripe = async () => {
    if (!stripeForm.secret_key.trim() && !stripeForm.publishable_key.trim() && !stripeForm.webhook_secret.trim())
      return toast.error("Nothing to save — paste at least one key");
    try {
      await api.put("/admin/settings", { stripe: stripeForm });
      toast.success("Stripe keys saved — new checkouts use these keys");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resetStripe = async () => {
    if (!confirmReset) { setConfirmReset(true); return; }
    try {
      await api.delete("/admin/settings/stripe");
      toast.success("Custom keys removed — back to the built-in sandbox");
      setConfirmReset(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-settings-page" className="max-w-3xl">
      <span className="nb-label">Platform</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Platform settings</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Fallback email server and payment keys for the whole platform. Schools can still set their own SMTP in their settings.</p>

      <div className="mt-8 nb-card p-6" data-testid="platform-smtp-card">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5" /> Fallback SMTP (notices & digests)</h2>
        <p className="text-sm text-zinc-500 mt-2">Used when a school hasn't configured its own SMTP server. Port 465 uses SSL; any other port uses STARTTLS.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="nb-label" htmlFor="super-smtp-host">SMTP host</label>
            <input id="super-smtp-host" className="nb-input mt-2" data-testid="super-smtp-host" placeholder="smtp.provider.com"
              value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="super-smtp-port">Port</label>
            <input id="super-smtp-port" type="number" className="nb-input mt-2" data-testid="super-smtp-port" placeholder="465"
              value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) || 465 })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="super-smtp-user">Username</label>
            <input id="super-smtp-user" className="nb-input mt-2" data-testid="super-smtp-user"
              value={smtp.user} onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="super-smtp-password">Password {smtpPwSaved && <span className="text-zinc-400">(saved — leave blank to keep)</span>}</label>
            <input id="super-smtp-password" type="password" className="nb-input mt-2" data-testid="super-smtp-password"
              value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="nb-label" htmlFor="super-smtp-from">From email</label>
            <input id="super-smtp-from" type="email" className="nb-input mt-2" data-testid="super-smtp-from" placeholder="noreply@mytimetable.co.uk"
              value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} />
          </div>
        </div>
        <button onClick={saveSmtp} data-testid="super-smtp-save" className="nb-btn bg-[#FDE68A] px-5 py-2.5 mt-5">
          <Save className="w-4 h-4" /> Save SMTP
        </button>
      </div>

      <div className="mt-6 nb-card p-6" data-testid="platform-stripe-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5" /> Stripe payment keys</h2>
          <span className={`px-3 py-1 rounded-lg border-2 border-zinc-900 text-xs font-bold ${stripeCfg.mode === "custom" ? "bg-[#A7E4D3]" : "bg-[#FDE68A]"}`} data-testid="stripe-mode-badge">
            {stripeCfg.mode === "custom" ? `Custom keys ··•${stripeCfg.secret_key_last4}` : "Built-in sandbox"}
          </span>
        </div>
        <p className="text-sm text-zinc-500 mt-2">
          Paste your own Stripe keys to take real payments. Leave blank to keep using the built-in test sandbox.
          Products and prices are recreated on your account automatically at the next checkout.
          Your webhook endpoint is <code className="font-bold">/api/stripe/webhook</code> — add it in your Stripe dashboard and paste its signing secret below.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="nb-label" htmlFor="stripe-secret">Secret key (sk_test_… or sk_live_…)</label>
            <input id="stripe-secret" type="password" className="nb-input mt-2" data-testid="stripe-secret-input" placeholder={stripeCfg.mode === "custom" ? `Saved ··•${stripeCfg.secret_key_last4} — paste to replace` : "sk_test_…"}
              value={stripeForm.secret_key} onChange={(e) => setStripeForm({ ...stripeForm, secret_key: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="stripe-publishable">Publishable key (optional)</label>
            <input id="stripe-publishable" className="nb-input mt-2" data-testid="stripe-publishable-input" placeholder="pk_test_…"
              value={stripeForm.publishable_key} onChange={(e) => setStripeForm({ ...stripeForm, publishable_key: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="stripe-webhook">Webhook signing secret (whsec_…){stripeCfg.webhook_secret_set && <span className="text-zinc-400"> (saved — paste to replace)</span>}</label>
            <input id="stripe-webhook" type="password" className="nb-input mt-2" data-testid="stripe-webhook-input" placeholder="whsec_…"
              value={stripeForm.webhook_secret} onChange={(e) => setStripeForm({ ...stripeForm, webhook_secret: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={verifyStripe} disabled={verifying} data-testid="stripe-verify-btn" className="nb-btn bg-[#7DD3FC] px-5 py-2.5">
            <ShieldCheck className="w-4 h-4" /> {verifying ? "Checking…" : "Verify key"}
          </button>
          <button onClick={saveStripe} data-testid="stripe-save-btn" className="nb-btn bg-[#A7E4D3] px-5 py-2.5">
            <Save className="w-4 h-4" /> Save keys
          </button>
          {stripeCfg.mode === "custom" && (
            <button onClick={resetStripe} data-testid="stripe-reset-btn" className="nb-btn bg-red-100 text-red-700 px-5 py-2.5">
              <RotateCcw className="w-4 h-4" /> {confirmReset ? "Click again to confirm reset" : "Reset to sandbox"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
